import { useNavigate } from 'react-router-dom'
import { MapPin } from 'lucide-react'
import PageContainer from '@/components/layout/PageContainer'
import StickySideBanner from '@/components/common/StickySideBanner'
import Button from '@/components/ui/Button'
import LocationSelector from '@/components/recommend/LocationSelector'
import { useCareCalculatorStore } from '@/stores/careCalculatorStore'
import SEO from '@/components/common/SEO'

/**
 * 문답 전 위치 입력.
 *
 * <p>위치만 받는다. 결과 화면의 "이 예산으로 갈 수 있는 시설"이 위치에 의존하기 때문이다.
 * 관계·연세는 계산에도 화면에도 쓰이지 않으므로 받지 않는다 — 쓰지 않는 개인정보는
 * 수집하지 않는다.</p>
 *
 * <p>체크리스트와 같은 {@code LocationSelector} 를 쓰고, 같은 이유로 <b>선택</b>으로 둔다.
 * 건너뛰면 결과 화면에서 시설을 찾을 때 그때 위치를 물어본다.</p>
 */
export default function CareCalculatorProfilePage() {
  const navigate = useNavigate()
  const { location, setLocation } = useCareCalculatorStore()

  return (
    <>
      {/* 좌우 세로배너 160x600 — 콘텐츠 max-w-2xl. 336+160+48(여백) = 544 */}
      <StickySideBanner slotKey="carecalc_side_left" side="left" topPx={96} edgeOffsetPx={544} breakpoint="xl" />
      <StickySideBanner slotKey="carecalc_side_right" side="right" topPx={96} edgeOffsetPx={544} breakpoint="xl" />
      {/* 진행 중 화면이라 색인 가치가 없다. 랜딩(/care-calculator)으로 신호를 모은다 */}
      <SEO title="돌봄 대상자 정보 입력" url="/care-calculator/profile" noindex />

      <PageContainer noMaxWidth>
        <div className="mx-auto flex max-w-2xl flex-col gap-6 py-6">
          <div>
            <h1 className="text-h2 font-bold text-cb-gray-900">
              어디서 돌보실 예정인가요?
            </h1>
            <p className="mt-2 text-body leading-relaxed text-cb-gray-600">
              주변 시설의 실제 비용을 함께 계산해 드려요.
            </p>
          </div>

          <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
            <div className="mb-3 flex items-start gap-2">
              <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-cb-primary/10">
                <MapPin className="h-4 w-4 text-cb-primary" />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-gray-900">
                  위치를 알려주세요{' '}
                  <span className="text-xs font-normal text-gray-400">(선택)</span>
                </h3>
                <p className="mt-0.5 text-xs leading-relaxed text-gray-500">
                  입력하지 않아도 계산은 진행됩니다.
                </p>
              </div>
            </div>
            <LocationSelector
              locationType={location?.type ?? null}
              addressText={location?.addressText ?? null}
              onSetLocation={(data) => setLocation(data)}
              onClearLocation={() => setLocation(null)}
            />
          </div>

          {/* 상단 서브헤더를 없앴으므로 되돌아갈 길을 여기에 둔다(문답 화면의 이전/다음과 같은 짝) */}
          <div className="flex gap-2">
            <Button
              size="lg"
              variant="secondary"
              onClick={() => navigate('/care-calculator')}
            >
              이전
            </Button>
            <Button size="lg" fullWidth onClick={() => navigate('/care-calculator/step')}>
              다음
            </Button>
          </div>
        </div>
      </PageContainer>
    </>
  )
}
